import React from 'react';
import i18n from '@/i18n';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  /** Changes when the page changes, so a crash on one page does not stick to the next. */
  resetKey?: string;
}

interface State {
  error: Error | null;
  componentStack: string;
  copied: boolean;
}

/*
 * The bundle's own file name (index-<hash>.js) is the build a crash came from.
 * Read from the page rather than baked in, so it can never disagree with what
 * actually ran.
 */
function bundleName(): string {
  const src = document.querySelector('script[type="module"][src*="/assets/index-"]')?.getAttribute('src') ?? '';
  return src.split('/').pop() ?? '';
}

/*
 * M-75, 2026-09-30: the "black square" Guillaume kept seeing is this screen, and
 * the lines that name the component that threw were only ever in his console.
 * Every crash is now sent to the server (/api/v1/client-crash, read on
 * /api/v1/admin/client-crashes), including the ones a quiet boundary
 * (fallback={null}) swallows -- those are the crashes nobody would ever report.
 */
function reportCrash(error: Error, componentStack: string) {
  try {
    const id = (window as unknown as { __clerk_user_id?: string }).__clerk_user_id ?? '';
    void fetch('/api/v1/client-crash', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(id ? { 'X-User-ID': id } : {}) },
      body: JSON.stringify({
        message: String(error?.message ?? error),
        stack: String(error?.stack ?? ''),
        component_stack: componentStack,
        path: window.location.pathname + window.location.search,
        build: bundleName(),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // A report that fails must never become a second crash.
  }
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, componentStack: '', copied: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const componentStack = info.componentStack ?? '';
    console.error('[ErrorBoundary]', error, componentStack);
    this.setState({ componentStack });
    reportCrash(error, componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, componentStack: '', copied: false });
    }
  }

  private details(): string {
    const e = this.state.error;
    return [
      e?.message ?? '',
      `page: ${window.location.pathname}`,
      `build: ${bundleName()}`,
      '',
      e?.stack ?? '',
      '',
      this.state.componentStack,
    ].join('\n');
  }

  private copy = () => {
    const text = this.details();
    navigator.clipboard?.writeText(text).then(
      () => this.setState({ copied: true }),
      () => this.setState({ copied: false }),
    );
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback !== undefined) return this.props.fallback;
      const button = { padding: '8px 16px', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#fff' } as const;
      return (
        <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif', color: '#fff', background: '#0b0b0f', minHeight: '100%' }}>
          <h2 style={{ marginBottom: 12 }}>{i18n.t('common:errorBoundary.title')}</h2>
          <p style={{ opacity: 0.8, marginBottom: 6 }}>{this.state.error.message}</p>
          <p style={{ opacity: 0.6, marginBottom: 16, fontSize: 13 }}>{i18n.t('common:errorBoundary.reported')}</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={() => this.setState({ error: null, componentStack: '', copied: false })}
              style={{ ...button, background: '#3B82F6' }}
            >
              {i18n.t('common:errorBoundary.retry')}
            </button>
            <button onClick={() => { window.location.href = '/'; }} style={{ ...button, background: '#334155' }}>
              {i18n.t('common:errorBoundary.home')}
            </button>
            <button onClick={this.copy} style={{ ...button, background: '#334155' }}>
              {this.state.copied ? i18n.t('common:errorBoundary.copied') : i18n.t('common:errorBoundary.copy')}
            </button>
          </div>
          <pre style={{ marginTop: 24, opacity: 0.5, fontSize: 12, overflow: 'auto', maxHeight: 300, whiteSpace: 'pre-wrap' }}>
            {this.state.error.stack}
            {this.state.componentStack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}
